interface Options {
  /**
   * Safety net: run the callback even if the browser never reports an idle
   * period. Also the delay used where `requestIdleCallback` is unavailable.
   */
  timeout?: number;
}

interface ScheduledIdleWork {
  cancel: () => void;
}

const DEFAULT_TIMEOUT = 2000;

/**
 * Runs `callback` during the browser's first idle period, falling back to a
 * timer where `requestIdleCallback` is unsupported (Safari < 18, jsdom).
 *
 * Used to keep non-urgent boot-time work — fetches for panels that start
 * closed, whole-client lookups nothing on screen needs yet — from competing
 * with the requests the visible page depends on. Prefer deferring over removing
 * when the result is read passively and would otherwise silently be empty.
 */
export function scheduleWhenIdle(
  callback: () => void,
  { timeout = DEFAULT_TIMEOUT }: Options = {},
): ScheduledIdleWork {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    const handle = window.requestIdleCallback(callback, { timeout });
    return {
      // `cancelIdleCallback` is checked separately from `requestIdleCallback`:
      // cancellation happens later, and the global may no longer be there (a
      // torn-down test environment, a partial polyfill).
      cancel: () => {
        if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(handle);
        }
      },
    };
  }

  const handle = setTimeout(callback, timeout);
  return { cancel: () => clearTimeout(handle) };
}
